import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { setCurrentSupabaseOrganization } from '@/lib/supabaseOrganization';
import type { CampaignRanking, CampaignRankingRawMetrics } from '../types/campaign.types';
import { calculateCampaignRanking } from '@/lib/algorithms/calculateCampaignRanking';
import { campaignKeys } from '../services/campaign.keys';
import { campaignRepository } from '../repositories/campaign.repository';

export const useCampaignPerformanceRanking = () => {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: campaignKeys.ranking(currentOrganization?.id),
    queryFn: async (): Promise<CampaignRanking[]> => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }

      await setCurrentSupabaseOrganization(currentOrganization.id);

      // Fetch campaigns and aggregated metrics via single consolidated RPC (no N+1)
      const campaignsData = await campaignRepository.getRankings(currentOrganization.id);
      if (!campaignsData || campaignsData.length === 0) return [];

      // Map raw data to standard metrics interface
      const metricsList = campaignsData.map((c) => {
        const totalViews = Number(c.total_views || 0);
        const totalEngagement = Number(c.total_engagement || 0);

        // Calculate timeline progress
        let timelineProgress = 0;
        const start = c.start_date ? new Date(c.start_date) : null;
        const end = c.end_date ? new Date(c.end_date) : null;
        const today = new Date();
        if (start && end && start.getTime() < end.getTime()) {
          const totalDays = end.getTime() - start.getTime();
          const elapsed = today.getTime() - start.getTime();
          timelineProgress = Math.min(100, Math.max(0, (elapsed / totalDays) * 100));
        }

        const rawMetrics: CampaignRankingRawMetrics = {
          totalViews,
          totalEngagement,
          spend: Number(c.spent_budget || 0),
          contractedDeliverables: 10,
          completedDeliverables: c.status === 'completed' ? 10 : 5,
          deliverableProgress: c.status === 'completed' ? 100 : 50,
          timelineProgress,
          paymentCompletionPercentage: c.status === 'completed' ? 100 : 50,
        };

        return {
          id: c.id,
          name: c.name,
          rawMetrics,
        };
      });

      // Find min/max values for normalization
      let minViews = Infinity;
      let maxViews = -Infinity;
      let minEngagement = Infinity;
      let maxEngagement = -Infinity;
      let minER = Infinity;
      let maxER = -Infinity;

      metricsList.forEach((m) => {
        const { totalViews, totalEngagement } = m.rawMetrics;
        const er = totalViews > 0 ? (totalEngagement / totalViews) * 100 : 0;

        minViews = Math.min(minViews, totalViews);
        maxViews = Math.max(maxViews, totalViews);
        minEngagement = Math.min(minEngagement, totalEngagement);
        maxEngagement = Math.max(maxEngagement, totalEngagement);
        minER = Math.min(minER, er);
        maxER = Math.max(maxER, er);
      });

      const safeMinMax = {
        minViews: minViews === Infinity ? 0 : minViews,
        maxViews: maxViews === -Infinity ? 0 : maxViews,
        minEngagement: minEngagement === Infinity ? 0 : minEngagement,
        maxEngagement: maxEngagement === -Infinity ? 0 : maxEngagement,
        minER: minER === Infinity ? 0 : minER,
        maxER: maxER === -Infinity ? 0 : maxER,
      };

      const rankings = metricsList.map((m) =>
        calculateCampaignRanking(m.id, m.name, m.rawMetrics, safeMinMax)
      );

      return rankings.sort((a, b) => b.overallScore - a.overallScore);
    },
    enabled: !!currentOrganization?.id,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
